import { bearerType, platforms, splitTypes } from "@/utils/constants";
import { sumBy } from "lodash";
import { createContext, useContext, useMemo } from "react";
import ty from "typy";

// Contexts and Hooks
export const ProductIntegrationContext = createContext();
export const useProductIntegration = () => useContext(ProductIntegrationContext);

export const MerchantConfigurationContext = createContext();
export const useMerchantConfiguration = () => useContext(MerchantConfigurationContext);

export const useIsPlatform = (targetProcessor) => {
    const {
        values: { platform },
    } = useProductIntegration();
    return useMemo(() => platform === targetProcessor, [platform, targetProcessor]);
};

//  Util Functions
const getParametersArr = (parameters) => {
    if (!parameters || !ty(parameters).isObject) return null;
    return Object.entries(parameters).map(([name, value]) => ({ name, value }));
};

const getParametersArrWithDescription = (parameters) => {
    if (!parameters || !ty(parameters).isObject) return [];
    return Object.entries(parameters).map(([name, value]) => ({
        name,
        value,
        description: "",
    }));
};

export const getTotalPercentage = (beneficiaries = [], splitType) => {
    if (splitType !== splitTypes.PERCENTAGE) return 0;
    return sumBy(beneficiaries, ({ amount }) => Number(amount));
};

export const splitMenu = [
    {
        value: splitTypes.FIXED,
        label: "Fixed",
    },
    {
        value: splitTypes.RELATIVE,
        label: "Relative",
    },
    {
        value: splitTypes.PERCENTAGE,
        label: "Percentage",
    },
];

export const transformProductIntegration = (formBody) => {
    const {
        beneficiaries,
        parameters,
        splitType,
        charge,
        isSplit,
        paymentFormFile,
        firstName,
        lastName,
        jobTitle,
        email,
        phoneNumber,
        schoolId,
        institutionId,
        productId,
        existingPaymentFormUrl,
        existingPaymentFormPath,
        ...restFormBody
    } = formBody;

    let requestBody = {
        ...restFormBody,
        schoolId,
        productName: productId,
        charge: Number(charge) || 0,
        shortName: institutionId,
    };

    // Nest contact information in contactInfo object
    if (firstName || lastName || jobTitle || email || phoneNumber) {
        requestBody.contactInfo = {
            ...(firstName && { firstName }),
            ...(lastName && { lastName }),
            ...(jobTitle && { jobTitle }),
            ...(email && { email }),
            ...(phoneNumber && { phoneNumber }),
        };
    }

    const rawParameters = parameters || {};
    const enrichedParameters = {
        ...rawParameters,
        USE_SPLIT: Boolean(isSplit),
    };

    if (Object.keys(enrichedParameters).length) {
        requestBody = { ...requestBody, parameters: getParametersArr(enrichedParameters) };
    }

    if (isSplit) {
        requestBody = {
            ...requestBody,
            splitDetails: {
                type: splitType,
                beneficiaries: beneficiaries?.map(({ amount, bank, ...restBeneficiary }) => ({
                    ...restBeneficiary,
                    bankCode: bank.code,
                    amount: Number(amount),
                })),
            },
        };
    }

    return {
        setupDto: requestBody,
        file: paymentFormFile,
    };
};

/**
 * Build setupDto for approved integration request-edit endpoint (JSON body, no file).
 * Do not include schoolId, productName, demo, or file.
 */
export const transformProductIntegrationToRequestEditDto = (formBody) => {
    if (!formBody || typeof formBody !== "object") return {};

    const {
        beneficiaries,
        parameters,
        splitType,
        charge,
        isSplit,
        firstName,
        lastName,
        jobTitle,
        email,
        phoneNumber,
        institutionId,
        description,
    } = formBody;

    const rawParameters = parameters || {};
    const enrichedParameters = {
        ...rawParameters,
        USE_SPLIT: Boolean(isSplit),
    };

    const setupDto = {
        shortName: institutionId ?? "",
        platform: toPlatformCode(formBody.platform),
        charge: Number(charge) || 0,
        description: description ?? "",
        parameters: getParametersArrWithDescription(enrichedParameters),
    };

    if (firstName || lastName || jobTitle || email || phoneNumber) {
        setupDto.contactInfo = {
            ...(firstName && { firstName }),
            ...(lastName && { lastName }),
            ...(jobTitle && { jobTitle }),
            ...(email && { email }),
            ...(phoneNumber && { phoneNumber }),
        };
    }

    if (isSplit && beneficiaries?.length) {
        setupDto.splitDetails = {
            type: splitType,
            beneficiaries: beneficiaries.map(({ amount, bank, ...restBeneficiary }) => ({
                ...restBeneficiary,
                bankCode: bank?.code ?? "",
                amount: Number(amount) ?? 0,
            })),
        };
    }

    return setupDto;
};

const getFileName = (filePath = "", fileUrl = "") => {
    if (filePath) {
        const pathParts = filePath.split("/");
        return pathParts[pathParts.length - 1] || "payment-form";
    }
    if (fileUrl) {
        const urlParts = fileUrl.split("/");
        return urlParts[urlParts.length - 1]?.split("?")[0] || "payment-form";
    }
    return "payment-form";
};

const getFileType = (filePath = "", fileUrl = "") => {
    const name = `${filePath || ""}${fileUrl || ""}`.toLowerCase();
    if (name.includes(".pdf")) return "application/pdf";
    if (name.includes(".png")) return "image/png";
    if (name.includes(".jpg") || name.includes(".jpeg")) return "image/jpeg";
    return "application/pdf";
};

const toPlatformCode = (platform) => {
    const rawValue =
        platform?.id ||
        platform?.value ||
        platform?.name ||
        platform?.description ||
        (typeof platform === "string" ? platform : "");
    const normalized = String(rawValue || "")
        .toUpperCase()
        .trim();
    if (Object.values(platforms).includes(normalized)) return normalized;
    return platforms.REMITA;
};

const toParametersObject = (rawParameters = []) => {
    if (!Array.isArray(rawParameters)) return {};
    return rawParameters.reduce((acc, parameter) => {
        const key = parameter?.configKey || parameter?.name || parameter?.key;
        if (!key) return acc;
        acc[key] = parameter?.value;
        return acc;
    }, {});
};

const toBeneficiaries = (splitDetails = {}) => {
    const { beneficiaries = [], type } = splitDetails;
    if (!Array.isArray(beneficiaries)) return [];
    return beneficiaries.map((beneficiary) => ({
        name: beneficiary.itemName || beneficiary.name || "",
        accountNumber: beneficiary.accountNum || beneficiary.accountNumber || "",
        bank: {
            code: beneficiary.bankCode || "",
            name: beneficiary.bankName || beneficiary.bankCode || "N/A",
        },
        amount:
            type === splitTypes.PERCENTAGE
                ? (beneficiary.percentage ?? beneficiary.amount ?? 0)
                : (beneficiary.amount ?? 0),
        tag: beneficiary.tag || "",
        deductChargeOn: Boolean(beneficiary.deductFrom),
        bearer: beneficiary.bearer || bearerType.CLIENT,
    }));
};

export const transformIntegrationRequestToCreateValues = (request = {}) => {
    if (!request || typeof request !== "object") return {};

    const contactInfo = request.contactInfo || {};
    const splitDetails = request.splitDetails || {};
    const isSplit = Boolean(splitDetails?.beneficiaries?.length);
    const platform = toPlatformCode(request.platform);
    const fileName = getFileName(request.filePath, request.fileUrl);
    const fileType = getFileType(request.filePath, request.fileUrl);

    return {
        description: request.description || "",
        productId: request.productName || "",
        institutionId: request.shortName || "",
        schoolId: request.schoolId || "",
        schoolName: request.shortName || "",
        customerName: request.schoolId || "",
        platform,
        parameters: toParametersObject(request.parameters),
        charge: request.charge ?? 0,
        isSplit,
        splitType: splitDetails.type || null,
        beneficiaries: toBeneficiaries(splitDetails),
        firstName: contactInfo.firstName || "",
        lastName: contactInfo.lastName || "",
        jobTitle: contactInfo.jobTitle || "",
        email: contactInfo.email || "",
        phoneNumber: contactInfo.phoneNumber || "",
        existingPaymentFormUrl: request.fileUrl || "",
        existingPaymentFormPath: request.filePath || "",
        paymentFormFile: request.fileUrl
            ? {
                  name: fileName,
                  type: fileType,
                  size: 1,
                  url: request.fileUrl,
                  isExistingFile: true,
              }
            : null,
    };
};
